import type { ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Badge from "@/v2-backoffice-client/components/badge"
import Link from "@/v2-backoffice-client/components/link"
import { buildCustomerViewLink } from "@/v2-backoffice-client/customer/customer.links"
import LicenseStatusBadge from "@/v2-backoffice-client/license/LicenseStatusBadge"
import { useLicenseGoverningStatuses } from "@/v2-backoffice-client/license/license.statuses.hooks"
import { LICENSE_SOURCE_OPERATOR } from "@/v2-backoffice-client/license/license.constants"
import LicenseCoverageIssueList from "@/v2-backoffice-client/license/coverage/LicenseCoverageIssueList"
import LicenseCoverageTimeline from "@/v2-backoffice-client/license/coverage/LicenseCoverageTimeline"
import {
  toneOfSeverity,
  type LicenseCoverageRow,
  type LicenseCoverageWindow,
} from "@/v2-backoffice-client/license/coverage/license.coverage.utils"

export type LicenseCoverageCardProps = ComponentProps<"article"> & {
  coverage: LicenseCoverageRow
  window: LicenseCoverageWindow
  showAxis?: boolean
  /** On the customer panel the customer is the context; the heading drops the name and the link. */
  onCustomerPanel?: boolean
}

const EDGE_CLASSES = {
  critical: "border-l-red-500",
  warning: "border-l-amber-500",
  info: "border-l-border",
  none: "border-l-transparent",
} as const

// One license on the Coverage board: who and what, the governing pill, the
// human cost this week, the timeline, and the issues with their levers.
function LicenseCoverageCard(props: LicenseCoverageCardProps) {
  const { coverage, window, showAxis, onCustomerPanel, className, ...rest } =
    props
  const { namesByCode } = useLicenseGoverningStatuses()
  const tone = toneOfSeverity(coverage.IssueSeverity)

  return (
    <article
      className={cn(
        "flex flex-col gap-3 rounded-md border border-l-4 bg-card px-4 py-3",
        EDGE_CLASSES[tone],
        className,
      )}
      data-feature-code="UI_BackofficeLicenseCoverage_Card"
      {...rest}
    >
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {!onCustomerPanel && (
          <h4 className="text-sm font-semibold">
            {coverage.ExternalCustomerId ? (
              <Link
                variant="unstyled"
                iconSrc={null}
                to={buildCustomerViewLink(
                  coverage.ExternalCustomerId,
                  "billing",
                )}
                className="hover:underline"
              >
                {coverage.CustomerName}
              </Link>
            ) : (
              coverage.CustomerName
            )}
          </h4>
        )}
        <span className="text-sm">
          {coverage.ProductName}
          {coverage.PlanName && coverage.PlanName !== coverage.ProductName && (
            <span className="text-muted-foreground"> · {coverage.PlanName}</span>
          )}
        </span>
        <LicenseStatusBadge
          size="xs"
          license={{
            LicenseStatusCode: coverage.LicenseStatusCode,
            TrialEndsAt: coverage.TrialEndsAt,
            StartedAt: coverage.StartedAt,
            RenewsAt: coverage.RenewsAt,
            CancelledAt: coverage.CancelledAt,
            PlanName: coverage.PlanName,
            LicenseSourceCode: coverage.LicenseSourceCode,
          }}
          statusName={namesByCode.get(coverage.LicenseStatusCode)}
        />
        <Badge variant="outline" size="xs">
          {coverage.LicenseSourceCode === LICENSE_SOURCE_OPERATOR
            ? "Operator"
            : "Fenerum"}
        </Badge>
        {coverage.Attempts7d > 0 && (
          <Badge variant="warning" size="xs" iconSrc="door-closed">
            {formatNumber(coverage.Users7d)}{" "}
            {coverage.Users7d === 1 ? "person" : "people"} ·{" "}
            {formatNumber(coverage.Attempts7d)} refused this week
          </Badge>
        )}
      </header>

      <LicenseCoverageTimeline
        segments={coverage.Segments}
        issues={coverage.Issues}
        window={window}
        showAxis={showAxis}
        compact={onCustomerPanel}
      />

      <LicenseCoverageIssueList
        coverage={coverage}
        hideCustomerLink={onCustomerPanel}
      />
    </article>
  )
}

export default LicenseCoverageCard
